import { ImgHTMLAttributes } from 'react';

interface ModernImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    loading?: 'lazy' | 'eager';
    sizes?: string;
    priority?: boolean;
    responsive?: boolean;
}

/**
 * ModernImage component that serves WebP and AVIF formats with PNG/JPEG fallbacks
 * Automatically generates multiple source formats for optimal performance
 */
const ModernImage = ({ 
    src, 
    alt, 
    width, 
    height, 
    className = "", 
    loading = "lazy",
    sizes = "(max-width: 320px) 280px, (max-width: 640px) 600px, (max-width: 960px) 900px, (max-width: 1280px) 1200px, 1600px",
    priority = false,
    responsive = true,
    ...props 
}: ModernImageProps) => {
    // Extract file extension and base path
    const lastDotIndex = src.lastIndexOf('.');
    const basePath = src.substring(0, lastDotIndex);
    const extension = src.substring(lastDotIndex + 1).toLowerCase();
    
    // Only apply modern format conversion to local images (not external URLs)
    const isLocalImage = !src.startsWith('http') && !src.startsWith('//');
    
    // Generate responsive srcSet for different sizes
    const generateResponsiveSrcSet = (format: string) => {
        if (!responsive) return undefined;
        
        const breakpoints = [320, 640, 960, 1280, 1920];
        return breakpoints
            .map(size => `${basePath}-${size}w.${format} ${size}w`)
            .join(', ');
    };

    if (!isLocalImage) {
        // For external images, just use regular img tag
        return (
            <img
                src={src}
                alt={alt}
                width={width}
                height={height}
                className={className}
                loading={priority ? 'eager' : loading}
                {...props}
            />
        );
    }

    // For local images that are PNG or JPEG, provide modern format sources
    if (extension === 'png' || extension === 'jpg' || extension === 'jpeg') {
        return (
            <picture>
                {/* AVIF format - best compression with responsive sizes */}
                <source 
                    srcSet={generateResponsiveSrcSet('avif') || `${basePath}.avif`} 
                    type="image/avif" 
                    sizes={responsive ? sizes : undefined}
                />
                
                {/* WebP format - good compression, wider support with responsive sizes */}
                <source 
                    srcSet={generateResponsiveSrcSet('webp') || `${basePath}.webp`} 
                    type="image/webp" 
                    sizes={responsive ? sizes : undefined}
                />
                
                {/* Original format fallback with responsive sizes */}
                <img
                    src={src}
                    srcSet={responsive ? generateResponsiveSrcSet(extension) : undefined}
                    alt={alt}
                    width={width}
                    height={height}
                    className={className}
                    loading={priority ? 'eager' : loading}
                    sizes={responsive ? sizes : undefined}
                    decoding="async"
                    {...props}
                />
            </picture>
        );
    }
    
    // For other formats (SVG, etc.), use regular img tag
    return (
        <img
            src={src}
            alt={alt}
            width={width}
            height={height}
            className={className}
            loading={priority ? 'eager' : loading}
            {...props}
        />
    );
};

export default ModernImage;